// A soft rising chord (C, E, G) when a 5C agent's message arrives in a live chat, drawn with the
// Web Audio API so there is no sound file to host. Each note has a few bell-like overtones and a
// short room echo, so it doesn't sound like a test beep. Browsers only start audio after the
// person has interacted with the page, so the audio is unlocked on their first tap or key press
// in Aura (they always type before an agent joins). If it stays locked, the chime is skipped.

let context = null;
let master = null;
let room = null;
let lastPlayedAt = 0;
const MIN_GAP_MS = 1000;  // a burst of messages chimes once
const VOLUME = 0.6;       // overall level; each note is quieter still

const audioContext = () => {
  if (context) return context;
  const AudioCtor = window.AudioContext || window.webkitAudioContext;
  if (!AudioCtor) return null;
  try {
    context = new AudioCtor();
  } catch (e) {
    context = null;
  }
  return context;
};

const unlock = () => {
  const ctx = audioContext();
  if (ctx && ctx.state === 'suspended') ctx.resume().catch(() => {});
};

export const initNotificationSound = () => {
  ['pointerdown', 'keydown', 'touchstart'].forEach((type) =>
    window.addEventListener(type, unlock, { passive: true }));
};

// The output, and a short soft room the notes ring into; built once
const outputs = (ctx) => {
  if (master) return;
  master = ctx.createGain();
  master.gain.value = VOLUME;
  master.connect(ctx.destination);
  room = ctx.createConvolver();
  const length = Math.floor(ctx.sampleRate * 0.9);
  const impulse = ctx.createBuffer(2, length, ctx.sampleRate);
  for (let channel = 0; channel < 2; channel += 1) {
    const data = impulse.getChannelData(channel);
    for (let i = 0; i < length; i += 1) data[i] = (Math.random() * 2 - 1) * Math.pow(1 - i / length, 3.2);
  }
  room.buffer = impulse;
  const wet = ctx.createGain();
  wet.gain.value = 0.22;
  room.connect(wet).connect(master);
};

// One soft struck note: the tone plus two quieter overtones, each fading on its own
const PARTIALS = [[1, 1], [2, 0.25], [3, 0.08]];
const strike = (ctx, start, frequency) => {
  const out = ctx.createGain();
  out.gain.value = 0.22;
  const filter = ctx.createBiquadFilter();
  filter.type = 'lowpass';
  filter.frequency.value = 6000;
  filter.Q.value = 0.5;
  filter.connect(out);
  out.connect(master);
  out.connect(room);
  PARTIALS.forEach(([ratio, level], i) => {
    const osc = ctx.createOscillator();
    const gain = ctx.createGain();
    const decay = 0.7 / (1 + i * 0.6);
    osc.type = 'sine';
    osc.frequency.value = frequency * ratio;
    gain.gain.setValueAtTime(0.0001, start);
    gain.gain.exponentialRampToValueAtTime(level, start + 0.004);
    gain.gain.exponentialRampToValueAtTime(0.0001, start + decay);
    osc.connect(gain).connect(filter);
    osc.start(start);
    osc.stop(start + decay + 0.05);
  });
};

// Never throws: a sound is a nicety, not part of the message
export const playMessageChime = () => {
  try {
    const now = Date.now();
    if (now - lastPlayedAt < MIN_GAP_MS) return;
    const ctx = audioContext();
    if (!ctx || ctx.state !== 'running') return;
    lastPlayedAt = now;
    outputs(ctx);
    const t = ctx.currentTime + 0.02;
    strike(ctx, t, 523.25);         // C5
    strike(ctx, t + 0.08, 659.25);  // E5
    strike(ctx, t + 0.16, 783.99);  // G5
  } catch (e) {
    /* no sound */
  }
};
